import anime from "animejs";

/**
 * Creates a zoom-in animation for tension mode.
 * Smoothly scales the container from 1.0 to the target scale over the given duration.
 * The easing accelerates so the zoom reaches peak as the final tension reel stops.
 *
 * @param {Object} target - PixiJS Container to animate (its .scale property)
 * @param {number} duration - Total zoom duration in ms (tensionReelCount × tensionCycleDuration)
 * @param {number} [maxScale=1.05] - Peak zoom scale
 * @returns {Object} animejs animation instance
 */
export const createTensionZoomAnimation = (
  target,
  duration,
  maxScale = 1.175,
) => {
  if (!target || !duration) return null;

  // Ensure we start from 1.0
  target.scale.x = 1.0;
  target.scale.y = 1.0;

  return anime({
    targets: target.scale,
    x: maxScale,
    y: maxScale,
    duration: duration,
    easing: "easeInOutQuad",
  });
};

/**
 * Creates a shaking animation for tension mode with built-in ramp in/out.
 * Uses percentage-based values relative to symbolHeight for responsive scaling.
 * Ported from Griffins Vault — adapted for bone-level shake in GameView.
 *
 * Returns a controller object:
 *   - target: the animated proxy object (read .x, .y, .rotate each frame)
 *   - stop(onComplete): ramps intensity to 0 then cleans up
 *   - kill(): immediately stops everything (for destroy/cleanup)
 *
 * @param {Object} target - Object with x, y, rotate properties to animate
 * @param {number} symbolHeight - Current symbol height for responsive scaling
 * @param {Object} [options] - Optional config
 * @param {number} [options.rampInDuration=400] - Ramp-in duration (ms)
 * @param {number} [options.rampOutDuration=300] - Ramp-out duration (ms)
 * @returns {Object} Controller with { target, stop, kill }
 */
export const createTensionShakeAnimation = (target, symbolHeight, options = {}) => {
  if (!target || !symbolHeight) return null;

  const {
    rampInDuration = 300,
    rampOutDuration = 600,
  } = options;


  // Floor at 10px so mobile screens always feel the shake regardless of symbolHeight
  const shakeAmount = Math.max(symbolHeight * 0.032, 14);

  // Earthquake keyframes — heavy stone pillars lurching side-to-side
  // More horizontal sway + higher rotation vs. original for a stronger quake feel
  const v = shakeAmount * 0.55; // vertical amplitude (tamed — avoid excessive bounce)
  const h = shakeAmount * 0.80; // horizontal amplitude (dominant — side-to-side sway)
  const r = 0.60;               // rotation amplitude (degrees — visible pillar wobble)

  // Internal proxy — shake loop animates this at full amplitude
  const _raw = { x: 0, y: 0, rotate: 0 };
  // Intensity envelope (0→1 on start, 1→0 on stop)
  const _envelope = { value: 0 };

  // Peaks pushed higher (1.2× vertical) for more contrast between lurch and settle
  const keyframes = [
    { x:  h * 1.0,  y:  v * 0.3,  rotate:  r * 0.8 },
    { x: -h * 1.0,  y: -v * 0.3,  rotate: -r * 0.8 },
    { x:  h * 0.7,  y:  v * 0.5,  rotate:  r * 0.5 },
    { x: -h * 0.7,  y: -v * 0.5,  rotate: -r * 0.5 },
    { x:  h * 0.9,  y:  v * 0.2,  rotate:  r * 1.0 },
    { x: -h * 0.9,  y: -v * 0.2,  rotate: -r * 1.0 },
    { x:  h * 0.5,  y:  v * 0.4,  rotate:  r * 0.4 },
    { x: -h * 0.5,  y: -v * 0.4,  rotate: -r * 0.4 },
  ];

  const offsets = keyframes.map((_, i) => (i / (keyframes.length - 1)) * 100);

  // Looping shake at full amplitude on the raw proxy — tighter cycle = more frantic
  const loopAnim = anime({
    targets: _raw,
    keyframes: offsets.map((offset, i) => ({
      offset: offset / 100,
      x: keyframes[i].x,
      y: keyframes[i].y,
      rotate: keyframes[i].rotate,
    })),
    duration: 460,
    easing: "linear",
    loop: true,
    update: () => {
      // Apply envelope-scaled values to the actual target every frame
      const i = _envelope.value;
      target.x = _raw.x * i;
      target.y = _raw.y * i;
      target.rotate = _raw.rotate * i;
    },
  });

  // Ramp in: 0 → 1 (faster hit — storm lands immediately)
  let rampAnim = anime({
    targets: _envelope,
    value: 1,
    duration: rampInDuration,
    easing: "easeOutCubic",
  });

  const kill = () => {
    if (rampAnim) { rampAnim.pause(); rampAnim = null; }
    loopAnim.pause();
    anime.remove(_raw);
    anime.remove(_envelope);
    target.x = 0;
    target.y = 0;
    target.rotate = 0;
  };

  const stop = (onComplete) => {
    if (rampAnim) { rampAnim.pause(); anime.remove(_envelope); }
    rampAnim = anime({
      targets: _envelope,
      value: 0,
      duration: rampOutDuration,
      easing: "easeInOutQuad",
      complete: () => {
        kill();
        if (onComplete) onComplete();
      },
    });
  };

  return { target, stop, kill };
};
